import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { IProductDataV3 } from '@df/catalog/product/product';
import { Status } from '@df/core/status';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { NProductConfiguration } from '@df/module-product-configuration/df-module-product-configuration.interfaces';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { TranslationService } from '@df/ui/translation/translation.service';
import { UiComponent } from '@df/ui/ui.component';
import { Product } from '../../product.decorator';
import { ButtonStyleComponent } from './../../../../common/buttons/button-style/button-style.component';
import { ResultComponent } from './../../../../common/result/result.component';
import { AbService } from '@df/module-ab/ab.service';

@Component({
    standalone: true,
    imports: [ButtonStyleComponent, ResultComponent],
    selector: 'product-view-add-to-basket',
    templateUrl: 'product-view-add-to-basket.html'
})
export class ProductViewAddToBasketComponent extends UiComponent {
    @Input()
    isDisabled = false;

    @Output()
    addedToBasket = new EventEmitter<void>();

    protected status = new Status();

    private productConfiguration = inject(ProductConfiguration);

    private translationService = inject(TranslationService);

    private scrollToService = inject(ScrollToService);

    private abService = inject(AbService);

    private isError = false;

    protected buttonCopy = '';

    protected warning?: string;

    protected get isButtonDisabled(): boolean {
        return this.isError || this.isDisabled;
    }

    private getTempStockWarning(): string | undefined {
        if (this.productConfiguration.product$.value.isTempOutOfStock) {
            return 'Notify me';
        }
        return undefined;
    }

    override ngOnInit(): void {
        this.productConfiguration.productRequestChange$.subscribe(() => this.onConfigurationChange());
        this.onConfigurationChange();
        super.ngOnInit();
    }

    private onConfigurationChange(): void {
        const error = this.getConfigurationError();
        this.buttonCopy = error || this.translationService.transform('add to cart');
        this.isError = !!error;
        this.warning = this.getWarning();
        this.detectChanges();
    }

    private getWarning(): string | undefined {
        const maxQty = this.productConfiguration.maxQty();
        if (maxQty > 0 && (maxQty < 0 || !this.productConfiguration.isSalable())) {
            return this.translationService.transform('Only % left', maxQty);
        }
        return undefined;
    }

    private getConfigurationError(): string | undefined {
        let errorMsg: string | undefined = undefined;
        this.productConfiguration.validate()?.find(error => {
            switch (error.code) {
                case NProductConfiguration.Error.Code.invalid:
                    errorMsg = this.translationService.transform('field % is not valid', error.fieldName);
                    return true;
                case NProductConfiguration.Error.Code.missing:
                    errorMsg = this.translationService.transform('please select %', error.fieldName);
                    return true;
                case NProductConfiguration.Error.Code.outOfStock:
                    errorMsg = this.translationService.transform('out of stock');
                    return true;
            }
        });

        return errorMsg ?? this.getTempStockWarning();
    }

    protected async submit(): Promise<void> {
        const warning = this.productConfiguration.validate();
        if (warning) {
            if (this.client.isS || this.client.isM) {
                this.scrollToService.scrollTo('#pdpConfigurableOptions', { offset: 100 });
                return;
            }
        }
        if (this.getTempStockWarning()) {
            alert('Implement notification!');
            return;
        }

        try {
            await this.productConfiguration.addToBasket(this.status);
            this.abService.triggerGoal('Add To Basket');
            this.addedToBasket.emit();
        } catch {
            // skip silently, we will display error using status
        }
    }

    protected get gridSize(): number {
        if (this.isArtPanel && this.client.sizeId > 1) {
            return 2;
        }
        return 1;
    }

    protected get isArtPanel(): boolean {
        return this.product.isArtPanel;
    }

    private get product(): Product<IProductDataV3> {
        return this.productConfiguration.product$.value as Product<IProductDataV3>;
    }
}
